/*******************************************************************************
 * $Id: multiSelect.js 18337 2018-12-21 16:14:14Z sthames $
 *******************************************************************************/
import angular from 'angular';
import uiBootstrap from 'angular-ui-bootstrap';
import csmTools from '../../util/tools/tools';
import multiSelectTemplate from './multiSelect.template.html';
import './multiSelect.css';

export default angular
  .module('csm.ui.multiSelect', [uiBootstrap, csmTools])

  /*******************************************************************************
   * Controller: csmMultiSelectController
   *
   * Controller for the multiSelect directive. This will populate the scope with
   * necessary utilities to manage the view. This also provides the following
   * utility functions on the controller instance:
   *
   *   getDisplayText - Function that returns the text displayed for a provided item
   *   getList        - Returns a reference to the list model
   *   init           - Function to initialize the ngModelController logic
   *   isListShown    - Returns indication that the list is currently shown
   *******************************************************************************/
  // ngInject
  .controller(
    'csmMultiSelectController',
    function ($scope, $timeout, $element, $compile, KEYCODES) {
      /** Item Display Template. */
      var ITEM_TEMPLATE =
        '<li class="item item-{0}" ng-click="toggle({0})">' +
        '<a href="" ng-class="{ \'selected\': isSelected({0})}">' +
        '<span class="marker"><span class="glyphicon glyphicon-ok"></span></span><span ng-class="getItemClass({0})" ng-bind="getLabel({0})"></span>' +
        '</a>' +
        '</li>';

      /** Dropdown menu reference. */
      var mMenu = angular.element(
        $element[0].querySelector('ul.uib-dropdown-menu')
      );

      /** Divider List Item Reference. */
      var mLiDivider = angular.element($element[0].querySelector('li.divider'));

      /** List Scope. Needed to cleanup watchers from compiled list item templates. */
      var mListScope;

      /** NgModel Controller. */
      var mModelController;

      /** Selected Items. */
      var mSelected = [];

      /** Property holder object. */
      $scope.props = { buttonLabel: 'Multi Select' };

      /*****************************************************************************
       * destroyListScope */
      /**
       * Destroys the list scope if it exists.
       *****************************************************************************/
      var destroyListScope = function () {
        if (mListScope) mListScope.$destroy();
      };

      /*****************************************************************************
       * getDisplayText */
      /**
       * Returns the display text for the item
       *
       * @param  item  Item to get the display text for
       * @return       String to display
       *****************************************************************************/
      this.getDisplayText = function (item) {
        return $scope.getLabel(null, item);
      };

      /*****************************************************************************
       * getItemClass */
      /**
       * Gives the ability to add a custom CSS class to item names in the selection list.
       * Returns a value to be utilized with the ng-class directive.
       *
       * @param  idx  Index of item being rendered in the list
       * @return      Result of itemClass expression for the rendered item
       *****************************************************************************/
      $scope.getItemClass = function (idx) {
        /*--------------------------------------------*/
        /* Identifies the object that is being looked */
        /* at for evaluation of adding a class.       */
        /*--------------------------------------------*/
        return $scope.itemClass({ $item: $scope.list[idx] });
      };

      /*****************************************************************************
       * getLabel */
      /**
       * Returns the display label for the given item index in the list. The
       * scope label expression is evaluated with a reference to the list item.
       * If nothing is returned, then the item will be used.
       *
       * @param  idx  List index to lookup
       * @return      String to display
       *****************************************************************************/
      $scope.getLabel = function (idx, item) {
        if (!item) item = $scope.list[idx];

        var label = $scope.label({ $item: item });
        return label || item.toString();
      };

      /*****************************************************************************
       * getList */
      /**
       * Returns the list of available items.
       *
       * @return List array
       *****************************************************************************/
      this.getList = function () {
        return $scope.list;
      };

      /*****************************************************************************
       * getPrompt */
      /**
       * Returns the text to display on the button. When no selections are made
       * the provided scope prompt is used otherwise a default prompt message.
       * When a single selection is made, the item label is set, otherwise when
       * multiple selections are present the multiple prompt label is used.
       *****************************************************************************/
      $scope.getPrompt = function () {
        return mSelected.length == 0
          ? $scope.selectPrompt || 'Choose a selection'
          : mSelected.length == 1
            ? $scope.getLabel(NaN, mSelected[0])
            : $scope.multiplePrompt() || 'Multiple Selected';
      };

      /*****************************************************************************
       * init */
      /**
       * Initialize the controller. This should be called from the link method of the
       * MultiSelect directive.
       *
       * @param ngModelController
       *****************************************************************************/
      this.init = function (ngModelController) {
        mModelController = ngModelController;

        /*------------------------------------------------------------*/
        /* Set the render function to reconcile the current list of   */
        /* viewValues against the mSelection list. If any view values */
        /* are not on the scope list, they are removed from the view. */
        /*------------------------------------------------------------*/
        mModelController.$render = function () {
          /*----------------*/
          /* Process values */
          /*----------------*/
          if (
            mModelController.$viewValue &&
            mModelController.$viewValue.length > 0
          ) {
            /*------------------------------*/
            /* Remove non selected elements */
            /*------------------------------*/
            for (var i = mSelected.length - 1; i > -1; i--) {
              var selected = mSelected[i];
              if (
                !mModelController.$viewValue.some(function (obj) {
                  return (
                    obj ==
                    ($scope.valueField ? selected[$scope.valueField] : selected)
                  );
                })
              )
                mSelected.splice(i, 1);
            }

            /*----------------------------*/
            /* Reconcile model selections */
            /*----------------------------*/
            for (i = mModelController.$viewValue.length - 1; i > -1; i--) {
              var match = ($scope.list || [])
                .filter(function (item) {
                  return (
                    mModelController.$viewValue[i] ==
                    ($scope.valueField ? item[$scope.valueField] : item)
                  );
                })
                .shift();
              if (match) {
                if (mSelected.indexOf(match) == -1) mSelected.push(match);
              } else mModelController.$viewValue.splice(i, 1);
            }
          } else
            /*--------------------------------------------*/
            /* Clear selected as there are no model items */
            /*--------------------------------------------*/
            mSelected = [];
        };

        /*----------------------------------------*/
        /* Add a model formatter function to      */
        /* ensure only array objects are rendered */
        /*----------------------------------------*/
        mModelController.$formatters.push(function (modelValue) {
          return !modelValue || angular.isArray(modelValue) ? modelValue : null;
        });
      };

      /*****************************************************************************
       * isListShown */
      /**
       * Returns an indication that the dropdown list is currently shown.
       *****************************************************************************/
      this.isListShown = function () {
        return $scope.props.isOpen;
      };

      /*****************************************************************************
       * isSelected */
      /**
       * Returns an indication if the current item for the list index is selected
       * by checking if the corresponding item is in the selected list.
       *
       * @param  idx  List index to lookup
       * @return      Boolean indication of selection
       *****************************************************************************/
      $scope.isSelected = function (idx) {
        return mSelected.indexOf($scope.list[idx]) > -1;
      };

      /*****************************************************************************
       * selectAll */
      /**
       * Adds all list items to the selection list.
       *****************************************************************************/
      $scope.selectAll = function () {
        mSelected = $scope.list.concat();
        updateModelView();
      };

      /*****************************************************************************
       * selectAll */
      /**
       * Clears all selections
       *****************************************************************************/
      $scope.selectNone = function () {
        mSelected = [];
        updateModelView();
      };

      /*****************************************************************************
       * showList */
      /**
       * Keydown listener for the button. If the down arrow is pressed, display
       * the list if its not already shown.
       *****************************************************************************/
      $scope.showList = function (event) {
        if ($scope.props.isOpen) return;

        if (event.keyCode == KEYCODES.DOWN_ARROW) $scope.props.isOpen = true;
      };

      /*****************************************************************************
       * toggle */
      /**
       * Toggles the item for the list index is selected.
       *****************************************************************************/
      $scope.toggle = function (idx) {
        var item = $scope.list[idx];
        var selectedIdx = mSelected.indexOf(item);

        if (selectedIdx == -1) mSelected.push(item);
        else mSelected.splice(selectedIdx, 1);

        updateModelView();
      };

      /*****************************************************************************
       * updateModelView */
      /**
       * Update the viewValue from the list of currently selected items. If a
       * valueField has been specified, use that property, otherwise the item
       * is used.
       *****************************************************************************/
      function updateModelView() {
        mModelController.$setViewValue(
          mSelected.map(function (item) {
            return $scope.valueField ? item[$scope.valueField] : item;
          })
        );
      }

      /*----------------------------------------------*/
      /* Set a watch on the list dataprovider so that */
      /* the appropriate li elements can be created   */
      /* and referenced for selection maintaining.    */
      /*----------------------------------------------*/
      $scope.$watchCollection('list', function (newList, oldList) {
        /*-----------------------*/
        /* Remove old list items */
        /*-----------------------*/
        destroyListScope();

        angular.element($element[0].querySelectorAll('li.item')).remove();

        /*---------------------------------------------------------------*/
        /* Create child scope from the directive for the templated items */
        /*---------------------------------------------------------------*/
        mListScope = $scope.$new(false);

        /*---------------------------------------------------------------*/
        /* Update selected list to contain elements in the new list only */
        /*---------------------------------------------------------------*/
        mSelected = mSelected.filter(function (item) {
          return newList && newList.indexOf(item) > -1;
        });

        /*-------------------------------------------------*/
        /* Add new elements by creating a string of list   */
        /* items and then compiling against the list scope */
        /*-------------------------------------------------*/
        if (newList)
          mLiDivider.after(
            $compile(
              angular.element(
                newList
                  .map(function (item, idx) {
                    return ITEM_TEMPLATE.format(idx);
                  })
                  .join('')
              )
            )(mListScope)
          );

        /*----------------------------------------------*/
        /* Update the width property for the drop down  */
        /* button so that it matches the content width. */
        /*----------------------------------------------*/
        $timeout(function () {
          $scope.width = mMenu.css('width');
        });
      });

      /*-------------------------------------------------------*/
      /* Destroy the list scope when this directive is removed */
      /*-------------------------------------------------------*/
      $scope.$on('$destroy', function () {
        destroyListScope();
      });
    }
  )

  /*******************************************************************************
   * Directive: multiSelectKeypress
   *
   * Utility directive to provided keyboard focusing to the elements in the list
   * which will update the uibDropdown controller for the active element.
   *
   * Note: Input char/key code processing will start with 49 as it seems that is
   * the start of display characters. Anything less are control keys and will be
   * ignored.
   *******************************************************************************/
  // ngInject
  .directive('multiSelectKeypress', function ($timeout, KEYCODES) {
    return {
      restrict: 'A',
      require: ['^^multiSelect', 'uibDropdown'],
      link: function (scope, element, attr, controllers) {
        /*-----------------------*/
        /* Controller References */
        /*-----------------------*/
        var mCtrlMultiSelect = controllers[0];
        var mCtrlDropdown = controllers[1];

        /** Menu List Reference. */
        var mMenuList = element[0].querySelector('.uib-dropdown-menu');

        /** Current Input Key Properties. */
        var mLastKeyCode;
        var mLastKeyIndex = -1;

        /*---------------------------------------------------------------------*/
        /* Add a listener for the keypress to process input characters such    */
        /* that the list will focus to those that begin with the input. If     */
        /* there are multiple matches, the focus will cycle amongst them. This */
        /* will also trigger any click events when the spacebar is pressed     */
        /*---------------------------------------------------------------------*/
        element.on('keypress', function (event) {
          /*-----------------------------------------------*/
          /* No processing happens when the list is closed */
          /*-----------------------------------------------*/
          if (!mCtrlMultiSelect.isListShown()) return;

          var code = event.charCode || event.keyCode;

          /*---------------------------------------*/
          /* Clear the current input when the code */
          /* changes or a control key is pressed   */
          /*---------------------------------------*/
          if (code < 49 || code != mLastKeyCode) mLastKeyIndex = -1;

          /*-------------------------------------------------------------*/
          /* Key press from the list will come from the anchor elements. */
          /* So we want to trigger the ngClick on the parent LI          */
          /*-------------------------------------------------------------*/
          if (code == KEYCODES.SPACE)
            $timeout(function () {
              angular.element(event.target).parent().triggerHandler('click');
            });
          /*-----------------------------*/
          /* Otherwise process input key */
          /*-----------------------------*/ else if (code >= 49) {
            mLastKeyCode = code;
            var char = String.fromCharCode(mLastKeyCode).toLowerCase();
            var idx = 0;

            /*-------------------------------------------------------------*/
            /* Resolve the bound list once. `list` is a two-way binding    */
            /* (list: '=') that can be undefined before the consumer       */
            /* populates it, so fall back to [] for every reference below  */
            /* (guarding only the .filter() left .indexOf()/.length able   */
            /* to throw the same TypeError).                               */
            /*-------------------------------------------------------------*/
            var list = mCtrlMultiSelect.getList() || [];

            /*---------------------------------*/
            /* Filter for any items that start */
            /* with the requested character    */
            /*---------------------------------*/
            var matches = list.filter(function (item) {
              return (
                mCtrlMultiSelect
                  .getDisplayText(item)
                  .toLowerCase()
                  .indexOf(char) == 0
              );
            });

            if (matches.length) {
              mLastKeyIndex = (mLastKeyIndex + 1) % matches.length;
              idx = list.indexOf(matches[mLastKeyIndex]);
            }

            /*------------------------------------------*/
            /* Do nothing if requested index is invalid */
            /*------------------------------------------*/
            if (idx > list.length - 1) return;

            /*----------------------------------------------------------*/
            /* Find the corresponding anchor element for the list index */
            /*----------------------------------------------------------*/
            var anchorElement = mMenuList.querySelector('.item-' + idx + ' a');
            anchorElement.focus();

            /*------------------*/
            /* Scroll into view */
            /*------------------*/
            mMenuList.scrollTop = anchorElement.offsetTop;

            /*-----------------------------------------------------*/
            /* Update Dropdown controller for selection index.     */
            /* The uibKeyboardnav directive only looks at anchor   */
            /* elements so that is what we will get the index from */
            /*-----------------------------------------------------*/
            var parentIdx = Array.prototype.indexOf.call(
              mMenuList.querySelectorAll('a'),
              anchorElement
            );
            mCtrlDropdown.selectedOption = parentIdx;
          }
        });
      }
    };
  })

  /*******************************************************************************
   * Directive: multiSelect
   *
   * @ngdoc     directive
   * @name      multiSelect
   * @memberof  csm.ui.multiSelect
   * @restrict  element
   *
   * @description
   * This provides a select-like component that provides for multiple selection
   * without the use of an additional key press. It also includes two control
   * options in the list to select all or none. This requires ngModel and will
   * populate the value with an array of selections. If a valueField has been
   * provided, the array will consist of the valueField property only.
   *
   * @attr {expression} itemClass      Optional expression to add special CSS classes to
   *                                   item name utilizing ng-class. This will be provided
   *                                   the current list item as $item.
   * @attr {expression} label          Optional expression to execute to determine display
   *                                   label. The current item is available as $item.
   *                                   Default is the item.
   * @attr {object}     list           List of objects to select from
   * @attr {expression} multiplePrompt Optional expression to execute for when multiple
   *                                   objects have been selected
   * @attr {string}     selectPrompt   Optional no selection label
   * @attr {string}     valueField     Property to use for setting ngModel values.
   *                                   If there is no value specified, the object
   *                                   is used.
   *******************************************************************************/
  // ngInject
  .directive('multiSelect', function ($compile, $timeout, KEYCODES) {
    return {
      restrict: 'E',
      require: ['multiSelect', 'ngModel'],
      template: multiSelectTemplate,
      scope: {
        itemClass: '&',
        label: '&',
        list: '=',
        multiplePrompt: '&',
        selectPrompt: '@',
        valueField: '@'
      },
      controller: 'csmMultiSelectController',
      link: function (scope, element, attr, ctrls) {
        /*----------------------------------------*/
        /* Initialize the multi select controller */
        /*----------------------------------------*/
        ctrls[0].init(ctrls[1]);
      }
    };
  }).name;
